import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react'
import { useCallback, useState } from 'react'
import { createVariants } from 'shared-ui/lib'
import { isNil, isNonNil } from 'utils'

import { SpriteMap, SpriteMap16 } from '../global'
import { Icon } from './Icon'

type Option = { name: string; id: number | string } | string | number

type GroupedPosition = 'start' | 'center' | 'end'

type Config<T> = {
  label?: string
  labelPosition?: 'inside' | 'outside'
  value?: T
  onChange: (value: T) => void
  groupedPosition?: GroupedPosition
  options: readonly T[] | T[]
  disabled?: boolean
  invalid?: boolean
  size?: 'xs' | 'sm' | 'lg' | 'fill'
  nameAccessor?: (value: T) => string
  nameFormatter?: (value: T) => ReactNode
  useFormatterInButton?: boolean
  placeholder?: string
  clearable?: boolean
  /** Footer content below the options; a function form receives `close` to dismiss the menu. */
  actions?: ReactNode | ((args: { close: () => void }) => ReactNode)
  icon?: ReactElement<ComponentPropsWithoutRef<typeof Icon>>
  badge?: ReactNode
}

// exported for testing
export type SelectMenuProps<T, U> = T extends Option
  ? Config<U>
  : Config<U> & {
      idAccessor: (value: U) => string | number
      nameAccessor: (value: U) => string
    }

const getId = <T extends Option>(value: T) =>
  typeof value === 'string' || typeof value === 'number' ? value : value.id

const getName = <T extends Option>(value: T) =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : value.name

const variants = {
  listboxButton: createVariants({
    base: 'group/select rounded-lg bg-white text-gray-900 cursor-pointer data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60 border-gray-200 data-[open]:border-green flex w-full max-w-full items-center justify-between gap-2 border px-3 py-1 h-10',
    variants: {
      flipped: {
        true: 'data-[open]:rounded-t-none',
        false: 'data-[open]:rounded-b-none'
      },
      size: {
        xs: 'h-8 min-w-[120px]',
        sm: 'min-w-[150px]',
        base: 'min-w-[200px]',
        lg: 'min-w-[400px]',
        fill: 'min-w-0'
      },
      styleDisabled: {
        true: 'text-gray-400'
      },
      invalid: {
        true: 'outline outline-offset-2 outline-red-600'
      },
      grouped: {
        start: 'rounded-t-lg rounded-b-none border-b-0 focus:z-10 relative',
        center: 'rounded-none border-b-0 border-t-1 focus:z-10 relative',
        end: 'rounded-b-lg rounded-t-none border-t-1 focus:z-10 relative'
      }
    }
  }),
  buttonOptionsDivider: createVariants({
    base: 'pointer-events-none sticky z-10 h-px shrink-0 bg-gray-200',
    variants: {
      optionsFlipped: {
        true: 'bottom-0 order-last',
        false: 'top-0'
      }
    }
  }),
  value: createVariants({
    base: 'pan-truncatable flex-1 text-left',
    variants: {
      placeholder: {
        true: 'pan-detail pr-1 italic text-gray-500'
      },
      boldValue: {
        true: 'pan-body-bold'
      }
    }
  }),
  label: createVariants({
    variants: {
      position: {
        inside: 'text-gray-500 font-normal',
        outside: 'pan-label mb-2 block'
      }
    }
  })
}

/**
 * Internal representation of no selection in the select menu.
 * This is necessary to ensure that the `value` prop is never `undefined`, while
 * also guaranteeing that only internal code can create an instance of this class,
 * so we won't falsely match against a real value in the options list.
 *
 */
class Nothing {}

const nothing = new Nothing()

export const PanoramaSelectMenu = <T,>(props: SelectMenuProps<T, T>) => {
  const {
    label,
    labelPosition = 'outside',
    value,
    onChange,
    groupedPosition,
    options,
    disabled = false,
    invalid = false,
    size = 'base',
    placeholder = 'select',
    useFormatterInButton = true,
    clearable = false,
    actions,
    icon,
    badge
  } = props

  const nameAccessor = isNonNil(props.nameAccessor)
    ? props.nameAccessor
    : (getName as (value: T) => string)
  const idAccessor =
    'idAccessor' in props ? props.idAccessor : (getId as (value: T) => string | number)
  const nameFormatter = isNonNil(props.nameFormatter) ? props.nameFormatter : nameAccessor

  const addIdIfNeeded = (value: T | undefined) =>
    typeof value === 'object' && value !== null && !('id' in value)
      ? { ...value, id: idAccessor(value) }
      : value

  const buttonDisplay = useFormatterInButton ? nameFormatter : nameAccessor
  const hasValue = isNonNil(value)

  const [optionsFlipped, setOptionsFlipped] = useState(false)
  const observePlacement = useCallback((node: HTMLElement | null) => {
    if (isNil(node)) return
    const readPlacement = () =>
      setOptionsFlipped((node.getAttribute('data-anchor') ?? '').startsWith('top'))
    readPlacement()
    const observer = new MutationObserver(readPlacement)
    observer.observe(node, { attributes: true, attributeFilter: ['data-anchor'] })
    return () => observer.disconnect()
  }, [])

  // State rather than a ref: `close` is handed to a render prop, and the ref lint rejects
  // ref-reading callbacks passed during render.
  const [actionsEl, setActionsEl] = useState<HTMLDivElement | null>(null)
  // Listbox exposes no imperative close, so replay its Escape dismissal path.
  const closeOptions = useCallback(() => {
    actionsEl?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
  }, [actionsEl])

  return (
    <Listbox
      value={addIdIfNeeded(value) ?? nothing}
      onChange={(next) => {
        onChange((next instanceof Nothing ? undefined : next) as T)
      }}
      disabled={disabled}
      as="div"
      className="w-full"
    >
      {isNonNil(label) && labelPosition === 'outside' && (
        <Label className={variants.label({ position: labelPosition })}>{label}</Label>
      )}
      <ListboxButton
        as="div"
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={variants.listboxButton({
          size,
          styleDisabled: disabled,
          invalid,
          grouped: groupedPosition,
          flipped: optionsFlipped
        })}
      >
        {label !== undefined && labelPosition === 'inside' && (
          <Label className={variants.label({ position: labelPosition })}>{label}</Label>
        )}
        {icon}
        <span
          className={variants.value({
            placeholder: !hasValue,
            boldValue: hasValue && label !== undefined && labelPosition === 'inside'
          })}
          title={hasValue ? nameAccessor(value) : placeholder}
        >
          {hasValue ? buttonDisplay(value) : placeholder}
        </span>
        {badge}
        {clearable && hasValue && (
          <button
            aria-label="Clear"
            type="button"
            className="flex-none cursor-pointer"
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onChange(undefined as T)
              }
            }}
            onClick={(e) => {
              e.stopPropagation()
              onChange(undefined as T)
            }}
          >
            <Icon icon={SpriteMap.CloseIcon} className="size-3 text-gray-300" />
          </button>
        )}
        <Icon
          icon={SpriteMap.ChevronIcon}
          className="size-3 flex-none rotate-180 text-gray-400 transition-transform group-data-[open]/select:rotate-0 group-data-[open]/select:text-green-500"
        />
      </ListboxButton>
      <ListboxOptions
        ref={observePlacement}
        modal={false}
        anchor={{ to: 'bottom start', gap: '-1px' }}
        as="div"
        className="z-[100] flex w-[--button-width] flex-col overflow-y-auto overscroll-none border border-green bg-white text-gray-900 [--anchor-max-height:10rem] focus:outline-none data-[anchor~=bottom]:rounded-b-lg data-[anchor~=bottom]:rounded-t-none data-[anchor~=top]:rounded-b-none data-[anchor~=top]:rounded-t-lg data-[anchor~=bottom]:border-t-0 data-[anchor~=top]:border-b-0 sm:[--anchor-max-height:20rem]"
      >
        <div aria-hidden className={variants.buttonOptionsDivider({ optionsFlipped })} />
        {options.map((o) => (
          <ListboxOption
            className="group/option pan-body flex min-h-12 max-w-full shrink-0 cursor-pointer items-center gap-2 px-4 py-2 text-left data-[selected]:pan-body-bold data-[active]:bg-gray-50 data-[selected]:bg-accent-100 data-[selected]:data-[active]:bg-accent-100 data-[selected]:text-green"
            key={idAccessor(o)}
            value={addIdIfNeeded(o)}
          >
            <span className="pan-truncatable" title={nameAccessor(o)}>
              {nameFormatter(o)}
            </span>
            <Icon
              icon={SpriteMap16.CheckCircle16}
              className="ml-auto hidden size-4 shrink-0 text-green group-data-[selected]/option:block"
            />
          </ListboxOption>
        ))}
        {isNonNil(actions) && (
          <div ref={setActionsEl} className="sticky bottom-0 flex shrink-0 gap-2 bg-white p-2">
            {typeof actions === 'function' ? actions({ close: closeOptions }) : actions}
          </div>
        )}
      </ListboxOptions>
    </Listbox>
  )
}
